import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '@tix-ticketing/ui-components';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { getApiCheckoutByEncryptedOrderIdOptions } from 'api/generated/@tanstack/react-query.gen';
import { useAppState } from 'lib/app-state';
import { useOrderRoutes } from 'lib/dedicated-order';
import { describeSubscriptionIssues } from 'lib/helpers/subscription-issue-helpers';

/**
 * The cart's way to checkout. Taking tickets off an order is deliberately never refused, so a
 * subscription can be left in a state only the payment attempt would have rejected — with the buyer
 * two pages away from the cart that could fix it. The checkout response is asked first and names
 * which subscription is wrong, so nothing moves and everything is still in reach.
 *
 * The mini cart passes `proceedToCheckout` its own navigate and so goes straight through: a drawer
 * has no room to explain this, and the checkout page reports the same issues on arrival.
 */
export function useGoToCheckout() {
  const router = useRouter();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {
    eventData,
    tokens
  } = useAppState();
  const routes = useOrderRoutes();
  const [isChecking, setIsChecking] = useState(false);

  // A split hands back the order that carries on to checkout, and its id is not always the one that
  // went in. Callers that have just split pass it, since app state has not caught up by then.
  const goToCheckout = useCallback(async (splitOrderId?: string | null) => {
    const encryptedOrderId = splitOrderId ?? eventData.order?.encryptedId;
    const push = () => router.push(tokens ? routes.checkout : routes.checkoutSignIn);

    // Only a subscription can produce one of these, and most orders carry none. Asking for those
    // would put a round trip in front of every buyer to rule out something they cannot have.
    const hasSubscription = (eventData.order?.subscriptionTypeGroups ?? []).length > 0;
    if (!encryptedOrderId || !hasSubscription) {
      push();
      return;
    }
    setIsChecking(true);
    try {
      const checkout = await queryClient.fetchQuery({
        ...getApiCheckoutByEncryptedOrderIdOptions({
          path: {
            encryptedOrderId
          }
        }),
        staleTime: 0
      });
      const messages = describeSubscriptionIssues(checkout.subscriptionIssues, intl);
      if (messages.length > 0) {
        setIsChecking(false);
        showToast({
          title: intl.formatMessage({
            id: "checkout.subscriptionIssue.title",
            defaultMessage: [{
              "type": 0,
              "value": "Your subscription is not complete"
            }]
          }),
          message: messages.join(' '),
          severity: 'warning'
        });
        return;
      }
    } catch {
      // Advisory only. Checkout reports the same issues on arrival and both close paths refuse
      // outright, so a pre-flight that cannot be made must not be what strands the buyer.
      setIsChecking(false);
    }

    // Left loading on purpose: the button stays spent across the route transition rather than
    // coming back to life in time for a second click to fire a second pre-flight and push.
    push();
  }, [eventData.order?.encryptedId, eventData.order?.subscriptionTypeGroups, intl, queryClient, router, routes, tokens]);
  return {
    goToCheckout,
    isChecking
  };
}